import React, { useEffect, useState } from 'react';
import { modals, paymentPlans, schoolYears, submitContractsJob } from 'ducks';
import { Button, Form } from 'react-bootstrap';
import './styles/BulkContractCreation.scss';
import Placeholder from 'components/Placeholder';
import FontAwesome from 'utils/FA';
import UnorderedList from 'components/shared/UnorderedList';
import { faClock } from '@fortawesome/pro-light-svg-icons/faClock';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faSync } from '@fortawesome/pro-light-svg-icons/faSync';
import PropTypes from 'prop-types';
import ModalComponent from '../../ModalComponent';
import Routes from '../../../../routes';
import { duckify, filterDuck, singleDuck } from '../../../utils/duckify';
import registeredComponent from '../../../utils/registeredComponent';

const ContractSubmission = (
  {
    availablePaymentPlans,
    schoolYear,
    verifyContacts,
    submitContracts,
    hideModal
  }
) => {
  const [currentPage, setPage] = useState(1);
  const [paymentPlan, setPaymentPlan] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [contactMessages, setContactMessages] = useState([]);
  const [contactsCount, setContactsCount] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(
    () => {
      verifyContacts().then(response => {
        setContactMessages(response.messages);
        setContactsCount(response.contract_count);
        setLoaded(true);
      });
    }, []
  );

  const page1 = () => {
    const beforeSubmitting = [
      'Verify payment plans are correct and have been tested',
      <a className='contract-fees' target='_blank' rel='noopener noreferrer' href={Routes.contract_fees_path()}>
        Verify Contract Fees and Rules
      </a>,
      'Preview the contract via the contact record'
    ];

    const aboutSubmission = [
      'Only contracts not yet completed will be submitted',
      'Any in-progress contracts will be replaced and any edits will be lost',
      'Submitted contracts will not change'
    ];

    return (
      <div className='contract-submission-first-page'>
        <UnorderedList
          message='Important'
          icon='info-circle'
          preface='Before submitting contracts, please do the following:'
          actions={beforeSubmitting}
          extraClassMessage='danger'
        />
        <UnorderedList
          message='About Contract Submission'
          className='contract-bulk-about'
          actions={aboutSubmission}
        />
        <ContactMessages messages={contactMessages} />
        <div className='contract-bulk-count-message'>
          <Placeholder.loading ready={loaded}>
            {contactsCount} contracts will be submitted
          </Placeholder.loading>
        </div>
      </div>
    );
  };

  const page2 = () => (
    <div className='contract-submission-second-page'>
      { availablePaymentPlans.length > 0
        && <PaymentPlanSelector plans={availablePaymentPlans} setter={setPaymentPlan} selected={paymentPlan} />}
      { availablePaymentPlans.length === 0 && schoolYear.billing_enabled
        && (
          <div className='alert alert-danger alert-block'>
            No payment plans are available. Plans must be configured in settings before contracts can be submitted.
          </div>
        )
      }
      <Form.Group
        controlId='submitConfirmationText'
      >
        <div className='contract-process-help-text'>
          {
            <Form.Label>
              <p>
                <FontAwesome icon={faClock} />
                <span> Once you confirm this action it may take a few minutes to complete.</span>
              </p>
              <p>
                <FontAwesome icon={faCheck} />
                <span> You can continue working while this process is running.</span>
              </p>
              <p>
                <FontAwesome icon={faSync} />
                <span>
                  {' Upon refresh, the Contract Status will indicate "Submitted" for each student, once complete.'}
                </span>
              </p>
            </Form.Label>
          }
        </div>
        <Form.Control
          name='confirmation-text-field'
          type='text'
          value={confirmation}
          placeholder='Enter SUBMIT'
          onChange={(e) => setConfirmation(e.target.value)}
          autoComplete='off'
        />
        <Form.Text muted>
          To confirm that you have verified the applicable fees and are ready to perform this action, type SUBMIT.
        </Form.Text>
      </Form.Group>
    </div>
  );

  const onSubmit = () => {
    hideModal();
    submitContracts(paymentPlan);
  };

  const submitDisabled = () => {
    return confirmation.toLowerCase() !== 'submit' || (paymentPlan === null && schoolYear.billing_enabled);
  };

  const buttonList = () => {
    let buttons = [];
    if (contactsCount > 0) {
      if (currentPage === 1) {
        buttons = [<Button className='btn' disabled={contactsCount < 1} onClick={() => setPage(2)}>Next</Button>];
      } else {
        buttons = [
          <Button className='btn' onClick={() => setPage(1)}>Back</Button>,
          <Button className='btn' disabled={submitDisabled()} onClick={onSubmit}>Submit</Button>
        ];
      }
    }
    return buttons;
  };

  return (
    <ModalComponent title='Submit Contracts' buttons={buttonList()}>
      {currentPage === 1 ? page1() : page2() }
    </ModalComponent>
  );
};

const PaymentPlanSelector = ({ plans, setter, selected }) => {
  const previousPlan = '_use_previous_payment_plan';
  return (
    <Form.Group className='submit_contract_form-group'>
      <Form.Label className='submit_contract_label'>Payment Plan</Form.Label>
      <div className='submit_contract_control'>
        {plans.map((plan) => (
          <Form.Check
            id={plan.canonical_name}
            key={plan.canonical_name}
            type='radio'
            label={plan.explanation}
            name='payment_plan'
            defaultChecked={selected === plan.canonical_name}
            value={plan.canonical_name}
            onChange={(e) => setter(e.target.value)}
          />
        ))
        }
        <Form.Check
          id={previousPlan}
          key={previousPlan}
          type='radio'
          label='Use previous payment plan'
          name='payment_plan'
          defaultChecked={selected === previousPlan}
          value={previousPlan}
          onChange={(e) => setter(e.target.value)}
        />
      </div>
    </Form.Group>
  );
};

const ContactMessages = ({ messages }) => {
  const linksToContacts = (contacts) => contacts.map((contactInfo) => (
    <a href={Routes.contact_path(`${contactInfo.id}`)}>{`${contactInfo.name}`}</a>
  ));

  return messages.map((messageInfo) => (
    <UnorderedList
      message={messageInfo.message}
      className={messageInfo.error ? 'danger' : 'warning'}
      extraClassMessage='smaller'
      actions={linksToContacts(messageInfo.contacts)}
    />
  ));
};

ContractSubmission.propTypes = {
  availablePaymentPlans: PropTypes.arrayOf(PropTypes.shape({
    canonical_name: PropTypes.string,
    explanation: PropTypes.string
  })),
  schoolYear: PropTypes.shape({
    billing_enabled: PropTypes.bool
  }).isRequired,
  verifyContacts: PropTypes.func.isRequired,
  submitContracts: PropTypes.func.isRequired,
  hideModal: PropTypes.func.isRequired
};

ContractSubmission.defaultProps = {
  availablePaymentPlans: []
};

PaymentPlanSelector.propTypes = {
  plans: PropTypes.arrayOf(PropTypes.shape({
    canonical_name: PropTypes.string,
    explanation: PropTypes.string
  })).isRequired,
  setter: PropTypes.func.isRequired,
  selected: PropTypes.string
};

PaymentPlanSelector.defaultProps = {
  selected: ''
};

ContactMessages.propTypes = {
  messages: PropTypes.arrayOf(PropTypes.shape({
    message: PropTypes.string,
    error: PropTypes.bool,
    contacts: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string
    }))
  }))
};

const mapDucksToProps = ({ schoolYearId }) => ({
  availablePaymentPlans: filterDuck(paymentPlans, { school_year_id: schoolYearId }),
  schoolYear: singleDuck(schoolYears, schoolYearId)
});

const mapDispatchToProps = (dispatch, { ids }) => ({
  verifyContacts: () => dispatch(submitContractsJob.actions.verifyContacts(ids)),
  submitContracts: (paymentPlan) => dispatch(
    modals.actions.show(
      'Downloader', {
        async: true,
        params: { contact_ids: ids, payment_plan: paymentPlan },
        url: '/api/user_jobs/submit_contracts_jobs',
        message: 'Contracts are being submitted. You can safely close this modal and continue working. A report of '
          + 'the results will automatically download when completed and will also be available on the User Jobs page.'
      }
    )
  ),
  hideModal: () => dispatch(modals.actions.hide())
});

export default registeredComponent('ContractSubmission')(duckify(mapDucksToProps, () => {}, mapDispatchToProps))(ContractSubmission);
